import React, { useState } from "react";
import { Icon, IconButton, Divider } from "@mui/material";
import toast from "react-hot-toast";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import ServicesApi from "../../Classes/API/Services/ServicesApi";
import ServiceSelector from "./ServiceSelector";

export default function UpgradeBundlePlan({ bundleId, onSuccess }) {
  const [selectedServices, setSelectedServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleAddServiceToQueue = (newService) => {
    setSelectedServices([...selectedServices, newService]);
  };

  const handleRemoveService = (index) => {
    setSelectedServices(selectedServices.filter((_, i) => i !== index));
  };

  const handleUpgrade = async () => {
    if (selectedServices.length === 0) return toast.error("Queue is empty");

    setSubmitting(true);
    const loadId = toast.loading("Updating bundle plan...");

    try {
      const payload = {
        bundleId,
        items: selectedServices.map((s) => ({
          serviceId: s.serviceId,
          unitPrice: s.unitPrice,
          frequency: s.frequency,
          quantity: s.quantity,
          numWeeks: s.numWeeks,
          selectedDays: s.selectedDays,
          startDate: s.startDate,
        })),
      };

      const res = await ServicesApi.UpgradeBundle(payload);

      if (res.status === "Success") {
        toast.success("Bundle upgraded successfully!", { id: loadId });
        setSelectedServices([]);
        if (onSuccess) onSuccess(); 
      } else {
        toast.error(res.message || "Failed to upgrade bundle", { id: loadId });
      }
    } catch (err) {
      toast.error("Network error, please try again", { id: loadId });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { Header: "Service", accessor: "name", width: "30%" },
    {
      Header: "Pattern",
      accessor: "frequency",
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="info"
          sx={{ textTransform: "uppercase" }}
        >
          {value}
        </MDTypography>
      ),
    },
    { Header: "Sessions", accessor: "quantity" },
    {
      Header: "Price/Unit",
      accessor: "unitPrice",
      Cell: ({ value }) => `${value.toLocaleString()} EGP`,
    },
    {
      Header: "Total",
      Cell: ({ row }) =>
        `${(
          row.original.unitPrice * row.original.quantity
        ).toLocaleString()} EGP`,
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <IconButton
          color="error"
          onClick={() => handleRemoveService(row.index)}
        >
          <Icon>delete_outline</Icon>
        </IconButton>
      ),
    },
  ];

  return (
    <MDBox p={1}>
      <MDBox mb={2}>
        <MDTypography variant="h5" fontWeight="bold">
          Upgrade Plan
        </MDTypography>
        <MDTypography variant="button" color="text">
          Add more recurring or daily services to the existing medical bundle.
        </MDTypography>
      </MDBox>

      <Divider sx={{ my: 3 }} />

      <MDBox mb={4}>
        <ServiceSelector
          onServiceAdd={handleAddServiceToQueue}
          actionIcon="add_task"
          actionColor="info"
        />
      </MDBox>

      <MDBox mt={3} sx={{ border: "1px solid #f0f2f5", borderRadius: "12px" }}>
        <DataTable
          table={{
            columns: columns,
            rows: selectedServices,
          }}
          entriesPerPage={false}
          showTotalEntries={false}
          noEndBorder
        />

        {selectedServices.length === 0 && (
          <MDBox py={6} textAlign="center">
            <MDTypography variant="button" color="secondary">
              No new services added to the upgrade queue.
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      <MDBox mt={4} display="flex" justifyContent="flex-end">
        <MDButton
          variant="gradient"
          color="success"
          size="large"
          disabled={selectedServices.length === 0 || submitting}
          onClick={handleUpgrade}
        >
          {submitting ? "Processing..." : "Confirm & Update Plan"}
        </MDButton>
      </MDBox>
    </MDBox>
  );
}
